// Reminder Service for automated notifications
import { supabase } from '../supabaseClient';
import { TelegramService } from './telegramService';

interface ReminderConfig {
  meal_reminder_time: string; // "18:00" for 6 PM
  reminder_frequency: 'daily' | 'weekly' | 'custom';
  is_active: boolean;
}

interface ClientReminder {
  id: string;
  trainer_id: string;
  telegram_user_id: string;
  client_name: string;
  reminder_type: 'meal_logging' | 'progress_check' | 'goal_review';
  reminder_time: string;
  is_active: boolean;
  last_sent?: string;
  next_send?: string;
}

export class ReminderService {
  /**
   * Send today's intake vs goals with simple food suggestions
   */
  static async sendDailyGoalSummary(telegramUserId: string, clientName: string): Promise<boolean> {
    try {
      const today = new Date();
      const start = new Date(today.toISOString().split('T')[0] + 'T00:00:00.000Z');

      // Get today's meals
      const { data: meals } = await supabase
        .from('Mastersheet')
        .select('calories, protein_g, carbs_g, fat_g, created_at')
        .eq('telegram_user_id', telegramUserId)
        .gte('created_at', start.toISOString());

      // Get goals
      const { data: goals } = await supabase
        .from('client_diet_goals')
        .select('target_calories, protein_target, carbs_target, fat_target')
        .eq('telegram_user_id', telegramUserId)
        .single();

      const totals = (meals || []).reduce(
        (acc, m) => ({
          calories: acc.calories + (m.calories || 0),
          protein: acc.protein + (m.protein_g || 0),
          carbs: acc.carbs + (m.carbs_g || 0),
          fat: acc.fat + (m.fat_g || 0)
        }),
        { calories: 0, protein: 0, carbs: 0, fat: 0 }
      );

      const g = goals || { target_calories: 0, protein_target: 0, carbs_target: 0, fat_target: 0 } as any;

      const gaps = {
        calories: Math.max(0, Math.round(g.target_calories - totals.calories)),
        protein: Math.max(0, Math.round(g.protein_target - totals.protein)),
        carbs: Math.max(0, Math.round(g.carbs_target - totals.carbs)),
        fat: Math.max(0, Math.round(g.fat_target - totals.fat))
      };

      const suggestions: string[] = [];
      if (gaps.protein > 20) suggestions.push('Add clean protein (e.g., chicken breast, Greek yogurt, tofu)');
      if (gaps.carbs > 30) suggestions.push('Add smart carbs (e.g., oats, rice, fruit)');
      if (gaps.fat > 10) suggestions.push('Add healthy fats (e.g., olive oil, nuts, avocado)');

      const message = `🎯 <b>Today vs Goals</b>\n\n` +
        `Calories: <b>${Math.round(totals.calories)}/${g.target_calories || 0}</b>\n` +
        `Protein: <b>${Math.round(totals.protein)}/${g.protein_target || 0}g</b>\n` +
        `Carbs: <b>${Math.round(totals.carbs)}/${g.carbs_target || 0}g</b>\n` +
        `Fat: <b>${Math.round(totals.fat)}/${g.fat_target || 0}g</b>\n\n` +
        (suggestions.length ? `Suggestions: ${suggestions.join(' • ')}\n\n` : '') +
        `Keep it up, ${clientName}!`;

      return await TelegramService.sendMessage(telegramUserId, message);
    } catch (e) {
      console.error('sendDailyGoalSummary error', e);
      return false;
    }
  }

  /**
   * Send today vs weekly average summary
   */
  static async sendDailyVsWeeklySummary(telegramUserId: string, clientName: string): Promise<boolean> {
    try {
      const now = new Date();
      const startToday = new Date(now.toISOString().split('T')[0] + 'T00:00:00.000Z');
      const sevenDaysAgo = new Date(now);
      sevenDaysAgo.setDate(now.getDate() - 7);

      const { data: recent } = await supabase
        .from('Mastersheet')
        .select('calories, protein_g, carbs_g, fat_g, created_at')
        .eq('telegram_user_id', telegramUserId)
        .gte('created_at', sevenDaysAgo.toISOString());

      const toTotals = (rows: any[]) => rows.reduce((acc, m) => ({
        calories: acc.calories + (m.calories || 0),
        protein: acc.protein + (m.protein_g || 0),
        carbs: acc.carbs + (m.carbs_g || 0),
        fat: acc.fat + (m.fat_g || 0)
      }), { calories: 0, protein: 0, carbs: 0, fat: 0 });

      const all = recent || [];
      const todayMeals = all.filter(r => new Date(r.created_at) >= startToday);
      const weekMeals = all.filter(r => new Date(r.created_at) < startToday);

      const today = toTotals(todayMeals);

      // Build per-day totals for the previous 7 days (excluding today)
      const perDay = new Map<string, { calories: number; protein: number; carbs: number; fat: number }>();
      const toLocalKey = (d: Date) => {
        const y = d.getFullYear();
        const mth = String(d.getMonth() + 1).padStart(2, '0');
        const day = String(d.getDate()).padStart(2, '0');
        return `${y}-${mth}-${day}`;
      };
      const keyFromAny = (value: any) => {
        if (value instanceof Date) return toLocalKey(value);
        if (typeof value === 'string') {
          const iso = new Date(value);
          if (!isNaN(iso.getTime())) return toLocalKey(iso);
          // Try dd/MM/YYYY, HH:mm:ss
          const match = value.match(/(\d{2})\/(\d{2})\/(\d{4})/);
          if (match) {
            const dd = parseInt(match[1], 10);
            const mm = parseInt(match[2], 10) - 1;
            const yyyy = parseInt(match[3], 10);
            const d = new Date(yyyy, mm, dd);
            if (!isNaN(d.getTime())) return toLocalKey(d);
          }
        }
        // Fallback to today to avoid crashing; still separates across retries
        return toLocalKey(new Date());
      };
      weekMeals.forEach((m: any) => {
        const key = keyFromAny(m.created_at);
        const prev = perDay.get(key) || { calories: 0, protein: 0, carbs: 0, fat: 0 };
        prev.calories += m.calories || 0;
        prev.protein += m.protein_g || 0;
        prev.carbs += m.carbs_g || 0;
        // some rows may store fat as fat or fat_g; prefer fat_g if present
        prev.fat += (typeof m.fat_g === 'number' ? m.fat_g : (m.fat || 0));
        perDay.set(key, prev);
      });

      // Build last 7 calendar days (excluding today) with zeros for missing days
      const last7Keys: string[] = [];
      for (let i = 1; i <= 7; i++) {
        const d = new Date(startToday);
        d.setDate(d.getDate() - i);
        last7Keys.push(toLocalKey(d));
      }

      const dayTotals = last7Keys.map(k => perDay.get(k) || { calories: 0, protein: 0, carbs: 0, fat: 0 });
      const denom = 7; // true 7-day average
      const weeklyAvg = dayTotals.reduce((acc, d) => ({
        calories: acc.calories + d.calories / denom,
        protein: acc.protein + d.protein / denom,
        carbs: acc.carbs + d.carbs / denom,
        fat: acc.fat + d.fat / denom
      }), { calories: 0, protein: 0, carbs: 0, fat: 0 });

      const msg = `📊 <b>Today vs Weekly Avg</b>\n\n` +
        `Calories: <b>${Math.round(today.calories)}</b> vs <i>${Math.round(weeklyAvg.calories)}</i>\n` +
        `Protein: <b>${Math.round(today.protein)}g</b> vs <i>${Math.round(weeklyAvg.protein)}g</i>\n` +
        `Carbs: <b>${Math.round(today.carbs)}g</b> vs <i>${Math.round(weeklyAvg.carbs)}g</i>\n` +
        `Fat: <b>${Math.round(today.fat)}g</b> vs <i>${Math.round(weeklyAvg.fat)}g</i>\n\n` +
        `You're doing great, ${clientName}!`;

      return await TelegramService.sendMessage(telegramUserId, msg);
    } catch (e) {
      console.error('sendDailyVsWeeklySummary error', e);
      return false;
    }
  }
  /**
   * Send meal logging reminder to a client
   */
  static async sendMealReminder(telegramUserId: string, clientName: string): Promise<boolean> {
    const message = `🍽️ <b>Meal Logging Reminder</b>\n\nHi ${clientName}! Don't forget to log your meals for today. It only takes a minute and helps track your progress! 💪\n\nReply with "done" when you've logged your meals.`;
    
    return await TelegramService.sendMessage(telegramUserId, message);
  }

  /**
   * Send progress check reminder
   */
  static async sendProgressReminder(telegramUserId: string, clientName: string): Promise<boolean> {
    const message = `📊 <b>Weekly Progress Check</b>\n\nHi ${clientName}! How are you feeling about your progress this week? Any wins or challenges you'd like to share?\n\nYour trainer is here to support you! 🎯`;
    
    return await TelegramService.sendMessage(telegramUserId, message);
  }

  /**
   * Send goal review reminder
   */
  static async sendGoalReminder(telegramUserId: string, clientName: string): Promise<boolean> {
    const message = `🎯 <b>Goal Review Time</b>\n\nHi ${clientName}! Let's check in on your goals. How are you progressing, and do you need any adjustments?\n\nKeep pushing towards your targets! 💪`;
    
    return await TelegramService.sendMessage(telegramUserId, message);
  }

  /**
   * Check if client has logged meals today
   */
  static async hasLoggedMealsToday(telegramUserId: string): Promise<boolean> {
    const today = new Date().toISOString().split('T')[0];
    
    const { data, error } = await supabase
      .from('Mastersheet')
      .select('id')
      .eq('telegram_user_id', telegramUserId)
      .gte('created_at', today)
      .limit(1);

    if (error) {
      console.error('Error checking meal logs:', error);
      return false;
    }

    return (data && data.length > 0);
  }

  /**
   * Get all active reminders for a trainer
   */
  static async getActiveReminders(trainerId: string): Promise<ClientReminder[]> {
    const { data, error } = await supabase
      .from('client_reminders')
      .select('*')
      .eq('trainer_id', trainerId)
      .eq('is_active', true);

    if (error) {
      console.error('Error fetching reminders:', error);
      return [];
    }

    return data || [];
  }

  /**
   * Create a new reminder for a client
   */
  static async createReminder(reminder: Omit<ClientReminder, 'id'>): Promise<boolean> {
    const { error } = await supabase
      .from('client_reminders')
      .insert(reminder);

    if (error) {
      console.error('Error creating reminder:', error);
      return false;
    }

    return true;
  }

  /**
   * Update reminder last sent time
   */
  static async updateLastSent(reminderId: string): Promise<boolean> {
    const { error } = await supabase
      .from('client_reminders')
      .update({ 
        last_sent: new Date().toISOString(),
        next_send: this.calculateNextSend()
      })
      .eq('id', reminderId);

    if (error) {
      console.error('Error updating reminder:', error);
      return false;
    }

    return true;
  }

  /**
   * Calculate next send time (24 hours from now)
   */
  private static calculateNextSend(): string {
    const next = new Date();
    next.setHours(next.getHours() + 24);
    return next.toISOString();
  }
} 